<template>
  <div class="dialogue-check">
    <div class="el-input-group el-input-group--append el-input-group--prepend">
      <div class="el-input-group__prepend">{{ prefix || 'Was' }}</div>

      <div v-if="errors.length" class="el-input-group__prepend">
        <help-tooltip type="error">
          <span v-for="error in errors" :key="error.message">{{ error.message }}</span>
        </help-tooltip>
      </div>

      <el-tooltip
        :disabled="!selectedContent"
        :visible-arrow="false"
        :content="selectedContent"
        placement="top"
      >
        <el-select
          v-close-on-zoom
          :value="state.selectedItem"
          :placeholder="placeholder"
          class="item-select"
          :disabled="readonly"
          @input="questionId => selectItem(questionId)"
        >
          <el-option
            v-for="item in getDialogueItems(emitter.caseConf)"
            :key="item.id"
            :label="getDialogueItemContent(item).en"
            :value="item.id"
          />
        </el-select>
      </el-tooltip>

      <div v-if="verb" class="el-input-group__append">{{ verb }}</div>
      <el-select
        id="operator-select"
        v-close-on-zoom
        :value="state.operator"
        placeholder="exactly"
        class="operator-select"
        :disabled="readonly"
        @input="operator => updateOperator(operator)"
      >
        <el-option v-for="op in operators" :key="op.value" :label="op.label" :value="op.value" />
      </el-select>
      <el-input
        v-integer-only
        type="number"
        placeholder="any amount of"
        :disabled="readonly"
        :value="state.count"
        :min="0"
        class="count-input"
        @change="value => updateCount(parseInt(value))"
      >
        <template slot="append">times</template>
      </el-input>
    </div>
    <br>
    <div v-if="aiEnabled" class="el-input-group el-input-group--append el-input-group--prepend">
      <div class="el-input-group__prepend">With confidence</div>
      <el-select
        v-close-on-zoom
        :value="state.confidenceOperator"
        class="confidence-operator-select"
        :disabled="readonly"
        @input="operator => updateConfidenceOperator(operator)"
      >
        <el-option v-for="op in confidenceOperators" :key="op.value" :label="op.label" :value="op.value" />
      </el-select>
      <el-input
        v-integer-only
        type="number"
        placeholder="Confidence"
        :disabled="readonly"
        :value="state.confidence"
        :min="1"
        :max="100"
        class="confidence-input"
        @change="value => updateConfidence(parseInt(value))"
      />
    </div>
  </div>
</template>

<script>
import { get, isNumber, uniqBy } from 'lodash';
import HelpTooltip from '../../common/components/HelpTooltip';

export default {
  name: 'dialogue-check',
  components: {
    HelpTooltip,
  },
  props: [
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
    'checkType',
    'placeholder',
    'prefix',
    'verb',
    'getDialogueItems',
    'getDialogueItemContent',
  ],
  data() {
    return {
      state: {
        selectedItem: null,
        count: null,
        operator: 'EQUAL',
        confidence: undefined,
        confidenceOperator: 'GREATER_THAN_OR_EQUAL',
        displayIndex: null,
      },
      operators: [
        { value: 'LESS_THAN', label: 'less than' },
        { value: 'EQUAL', label: 'exactly' },
        { value: 'GREATER_THAN', label: 'more than' },
      ],
      confidenceOperators: [
        { value: 'LESS_THAN', label: '<' },
        { value: 'LESS_THAN_OR_EQUAL', label: '≤' },
        { value: 'GREATER_THAN_OR_EQUAL', label: '≥' },
        { value: 'GREATER_THAN', label: '>' },
      ],
    };
  },
  computed: {
    selectedContent() {
      const items = this.getDialogueItems(this.emitter.caseConf) || [];
      const item = items.find(i => i.id === this.state.selectedItem);
      const content = item ? this.getDialogueItemContent(item) : null;
      return content ? content.en : '';
    },
    errors() {
      if (!this.emitter.$store) {
        return [];
      }
      const errors = get(
        this.emitter.$store.getters['validation/errorsByFieldAndId'],
        `caseLogicDescription.${this.control.parent.id}`,
        [],
      ).filter(error => error.metadata && error.metadata.checkKey === this.ikey);

      return uniqBy(errors, 'message');
    },
    aiEnabled() {
      return this.checkType === 'AI_QUESTION';
    },
  },
  mounted() {
    this.syncState();
    this.emitter.on('process', (nodeOrControl) => {
      // Update internal state when the node data changes
      // outside (e.g. from a CallAction)
      if (nodeOrControl === this.control) {
        this.syncState();
      }
    });
    this.update();
  },
  methods: {
    syncState() {
      const data = this.getData(this.ikey);
      this.state.selectedItem = get(data, 'id');
      this.state.count = get(data, 'count');
      this.state.operator = get(data, 'operator') || 'EQUAL';
      if (this.aiEnabled) {
        this.state.confidence = get(data, 'confidence');
        this.state.confidenceOperator = get(data, 'confidenceOperator') || 'GREATER_THAN_OR_EQUAL';
      }
      this.state.displayIndex = this.control.displayIndex;
    },
    selectItem(itemId) {
      this.state.selectedItem = itemId;
      if (this.aiEnabled && !this.state.confidence) {
        this.state.confidence = 70; // Default confidence
      }
      this.update();
    },
    updateCount(count) {
      if (isNumber(count)) {
        this.state.count = count;
      } else {
        this.state.count = null;
      }
      this.update();
    },
    updateOperator(operator) {
      this.state.operator = operator;
      this.update();
    },
    updateConfidence(confidence) {
      this.state.confidence = confidence;
      this.update();
    },
    updateConfidenceOperator(operator) {
      this.state.confidenceOperator = operator;
      this.update();
    },
    update() {
      const data = {
        type: this.checkType,
        id: this.state.selectedItem,
        operator: this.state.operator,
        value: true,
        count: this.state.count,
        displayIndex: this.state.displayIndex,
      };
      if (this.aiEnabled) {
        data.confidence = this.state.confidence;
        data.confidenceOperator = this.state.confidenceOperator;
      }
      this.putData(this.ikey, data);
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.el-input-group {
  width: auto !important;
}
.operator-select {
  width: 110px;
}
.count-input {
  width: 190px;
}
.confidence-input {
  width: 160px;
}
.confidence-operator-select {
  width: 70px;
}
</style>
